// @flow
import { isArray } from 'lodash';
import type { Action, State, LeagueTeams } from './types';

const initialState: State = {
  games: {
    shelves: { 'ALL SPORTS': [] },
    rawGames: [],
    fetching: false,
    error: false,
  },
  myTeams: {
    teamIds: [],
    games: [],
    teams: [],
    fetching: false,
    error: false,
  },
  allTeams: {
    teamsByLeague: {},
    teamsById: {},
    fetching: false,
    error: false,
  },
  leagues: {
    fetching: false,
    items: [],
    error: false,
  },
  saveTeams: {
    fetching: false,
    error: false,
  },
  gameStats: {
    error: false,
    fetching: false,
    stats: {},
  },
  currentFilters: {
    day: 'Today',
    sport: 'ALL SPORTS',
    shelfFilters: {},
  },
};

const buildMyGames = (teams, games) => {
  const cleanedTeamIds = teams.filter(teamId => teamId !== null);
  return games.filter(game => {
    const { away_team: away, home_team: home } = game;

    return cleanedTeamIds.includes(away.team_id) || teams.includes(home.team_id);
  });
};

const consolidateTeams = (teamsByLeague: LeagueTeams) => {
  const allTeams = {};
  const leagues = Object.keys(teamsByLeague);

  leagues.forEach(league => {
    teamsByLeague[league].forEach(team => {
      team.league = league;
      allTeams[team.id] = team;
    });
  });

  return allTeams;
};

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case 'REQUEST_GAMES':
      return { ...state, games: { ...state.games, fetching: true } };

    case 'RECEIVE_GAMES': {
      const { shelves, rawGames } = action;
      const games = { error: false, fetching: false, shelves, rawGames };

      const myTeams = { ...state.myTeams };

      if (myTeams.teamIds.length > 0) {
        myTeams.games = buildMyGames(myTeams.teamIds, rawGames);
      }

      return { ...state, games, myTeams };
    }

    case 'RECEIVE_GAMES_ERROR':
      return {
        ...state,
        games: { fetching: false, shelves: { 'ALL SPORTS': [] }, error: true, rawGames: [] },
      };

    case 'REQUEST_MY_TEAMS':
      return { ...state, myTeams: { ...state.myTeams, fetching: true } };

    case 'RECEIVE_MY_TEAMS': {
      const myTeams = {
        error: false,
        fetching: false,
        teamIds: isArray(action.teams) ? action.teams : [],
        games: [],
        teams: [],
      };

      const games = { ...state.games };

      if (games.rawGames.length > 0) {
        myTeams.games = buildMyGames(myTeams.teamIds, games.rawGames);
      }

      const { teamsById } = state.allTeams;

      if (teamsById && Object.keys(teamsById).length > 0) {
        myTeams.teams = myTeams.teamIds.map(teamId => teamsById[teamId]).filter(team => team);
      }

      return { ...state, myTeams, games };
    }

    case 'RECEIVE_MY_TEAMS_ERROR':
      return {
        ...state,
        myTeams: { fetching: false, teamIds: [], teams: [], games: [], error: true },
      };

    case 'REQUEST_ALL_TEAMS':
      return { ...state, allTeams: { ...state.allTeams, fetching: true } };

    case 'RECEIVE_ALL_TEAMS': {
      const myTeams = { ...state.myTeams };
      const allTeams = {
        fetching: false,
        error: false,
        teamsByLeague: action.teams,
        teamsById: consolidateTeams(action.teams),
      };

      if (myTeams.teamIds.length > 0) {
        myTeams.teams = myTeams.teamIds
          .map(teamId => allTeams.teamsById[teamId])
          .filter(team => team);
      }

      return { ...state, myTeams, allTeams };
    }

    case 'RECEIVE_ALL_TEAMS_ERROR':
      return {
        ...state,
        allTeams: { fetching: false, teamsByLeague: null, teamsById: null, error: true },
      };
    case 'REQUEST_LEAGUES':
      return { ...state, leagues: { items: [], fetching: true, error: true } };
    case 'RECEIVE_LEAGUES':
      return { ...state, leagues: { error: false, fetching: false, items: action.leagues } };
    case 'RECEIVE_LEAGUES_ERROR':
      return { ...state, leagues: { items: [], fetching: false, error: true } };
    case 'REQUEST_SAVE_MY_TEAMS':
      return { ...state, saveTeams: { fetching: true, error: false } };
    case 'RECEIVE_SAVE_MY_TEAMS':
      return { ...state, saveTeams: { fetching: false, error: false } };
    case 'RECEIVE_SAVE_MY_TEAMS_ERROR':
      return { ...state, saveTeams: { fetching: false, error: true } };
    case 'REQUEST_GAME_STATS':
      return { ...state, gameStats: { stats: {}, fetching: true, error: false } };
    case 'RECEIVE_GAME_STATS':
      return { ...state, gameStats: { stats: action.stats, fetching: false, error: false } };
    case 'RECEIVE_GAME_STATS_ERROR':
      return { ...state, gameStats: { stats: {}, fetching: false, error: true } };
    case 'SAVE_SPORTS_FILTERS':
      return {
        ...state,
        currentFilters: action.payload,
      };
    default:
      /* Following line assures switch exhaustiveness */
      (action.type: empty); /* eslint-disable-line */
      return state;
  }
};

export default reducer;
